import {
  advancedFormControl,
  FormControlTypeEnum,
} from '@magieno/common';
import { translationBaseKey } from '../../../../../decorators/translation-base-key.decorator';
import { IsBoolean, IsNotEmpty, IsOptional, validateIf } from '@pristine-ts/class-validator';
import { CompanyTypeEnum } from '../../../../../enums/company-type.enum';
import { CorporateIncomeTaxFederalOptions } from '../federal/corporate-income-tax-federal.options';
import { clonePropertyMetadata } from '@pristine-ts/metadata';
import { FieldTypeEnum } from '../../../../../enums/field-type.enum';
import { ErrorMessageBuilder } from '../../../../../builders/error-message.builder';

@translationBaseKey('corporate-tax-federal-in-corporate-income-tax-options')
export class CorporateIncomeTaxFederalInCorporateIncomeTaxOptions implements Omit<CorporateIncomeTaxFederalOptions,
  'calculationKeyname' |
  'fiscalYear' |
  'fiscalYearStart' |
  'fiscalYearEnd' |
  'companyType' |
  'totalTaxableCapital' |
  'businessIncome' |
  'adjustedInvestmentIncome' |
  'donations' |
  'capitalLossBalance' |
  'nonCapitalLossBalance' |
  'assignedBusinessLimit' |
  'interestIncome' |
  'connectedCorporations'> {

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'nonEligibleDividendNonConnected')
  nonEligibleDividendNonConnected: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'eligibleDividendNonConnected')
  eligibleDividendNonConnected: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'capitalGain')
  capitalGain: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'capitalLoss')
  capitalLoss: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'businessIncomeOutsideCanada')
  businessIncomeOutsideCanada: number = 0;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'previousIMRTDND')
  @IsOptional()
  previousIMRTDND?: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'previousRTDND')
  @IsOptional()
  previousRTDND?: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'nonEligibleDividendPaid')
  nonEligibleDividendPaid: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'previousIMRTDD')
  @IsOptional()
  previousIMRTDD?: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'previousRTDD')
  @IsOptional()
  previousRTDD?: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'eligibleDividendPaidLastYear')
  @validateIf((object: CorporateIncomeTaxFederalInCorporateIncomeTaxOptions, root, currentPath) => {

    if (root.hasOwnProperty('isEligibleDividendMaximized') === false ||
      typeof root['isEligibleDividendMaximized'] !== 'boolean' ||
      root.hasOwnProperty('corporateIncomeTaxOptionsForm') === false ||
      root['corporateIncomeTaxOptionsForm'].hasOwnProperty('companyType') === false
    ) {
      return false;
    }

    const isEligibleDividendMaximized: boolean = root['isEligibleDividendMaximized'];
    const companyType = root['corporateIncomeTaxOptionsForm']['companyType'];

    return companyType === CompanyTypeEnum.ccpcAllYear && isEligibleDividendMaximized;
  })
  eligibleDividendPaidLastYear?: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'gRIPBalance')
  @validateIf((object: CorporateIncomeTaxFederalInCorporateIncomeTaxOptions, root, currentPath) => {

    if (root.hasOwnProperty('isEligibleDividendMaximized') === false ||
      typeof root['isEligibleDividendMaximized'] !== 'boolean' ||
      root.hasOwnProperty('corporateIncomeTaxOptionsForm') === false ||
      root['corporateIncomeTaxOptionsForm'].hasOwnProperty('companyType') === false
    ) {
      return false;
    }

    const isEligibleDividendMaximized: boolean = root['isEligibleDividendMaximized'];
    const companyType = root['corporateIncomeTaxOptionsForm']['companyType'];

    return companyType === CompanyTypeEnum.ccpcAllYear && isEligibleDividendMaximized;
  })
  gRIPBalance?: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'eligibleDividendPaid')
  eligibleDividendPaid: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'lRIPBalance')
  @IsOptional()
  lRIPBalance?: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Voulez-vous prendre en compte l\'impôt de la partie IV?',
        en: 'Do you want to take Part IV tax into account',
      },
      tooltip: {
        fr: 'L\'impôt de la partie IV est un impôt remboursable. Il s\'agit essentiellement d\'un impôt payé par une société privée sur les dividendes reçus d\'autres sociétés',
        en: 'Part IV Tax is a refundable tax. It\'s essentially a tax paid by a private corporation on dividends received from other corporations.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isPartIVTaxUsed: boolean;
}

